import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Chip, TableCell, TableRow, Typography } from "@mui/material";
import { Verified } from "@mui/icons-material";

import TruncateText from "../../../components/TruncateText";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import messages from "../../../assets/locale/messages";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import noImporterImg from "../../../assets/images/placeholders/noImportersPlaceholderImg.svg";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { EXPORTER_STATUS } from "../../../utils/Constants";
import { combinedListRequest } from "../../../store/Users/actions";
import {
  categoriesLookupRequest,
  countriesLookupRequest,
  pricingPlansLookupRequest
} from "../../../store/Lookups/actions";

const Combined = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { users } = messages[lang];

  const combinedList = useSelector(
    (state) => state.users.combined.list
  );
  const combinedMeta = useSelector(
    (state) => state.users.combined.meta
  );
  const categoriesLookup = useSelector(
    (state) => state.lookups.categories.list
  );
  const countriesLookup = useSelector(
    (state) => state.lookups.countries.list
  );
  const pricingPlansLookup = useSelector(
    (state) => state.lookups.pricingPlans.list
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilterValue, setCategoryFilterValue] = useState([]);
  const [countryFilterValue, setCountryFilterValue] = useState([]);
  const [businessFilterValue, setBusinessFilterValue] = useState([]);
  const [statusFilterValue, setStatusFilterValue] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [paramsObject, setParamsObject] = useState({
    country_ids: [],
    category_ids: [],
    business_type_ids: [],
    status_ids: [],
    subscriptionPlan_ids: []
  });
  const [pricingPlanFilterValue, setPricingPlanFilterValue] =
    useState([]);

  useEffect(() => {
    dispatch(categoriesLookupRequest());
    dispatch(countriesLookupRequest());
    dispatch(pricingPlansLookupRequest());
  }, []);

  const requestCombinedList = () => {
    dispatch(
      combinedListRequest({
        params: {
          page,
          search_value: searchValue,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    if (clearFilters) {
      setCategoryFilterValue([]);
      setCountryFilterValue([]);
      setBusinessFilterValue([]);
      setStatusFilterValue([]);
      setPricingPlanFilterValue([]);
      setParamsObject({
        country_ids: [],
        category_ids: [],
        business_type_ids: [],
        status_ids: [],
        subscriptionPlan_ids: []
      });
      setClearFilters(false);
    }
  }, [clearFilters]);

  useEffect(() => {
    requestCombinedList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestCombinedList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        country_ids: countryFilterValue,
        category_ids: categoryFilterValue,
        business_type_ids: businessFilterValue,
        status_ids: statusFilterValue.filter((id) => id !== 6),
        isActive: statusFilterValue.includes(6) ? false : null,
        subscriptionPlan_ids: pricingPlanFilterValue
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  const COMBINED_STATUS_FILTER = [
    {
      id: 1,
      value: users.combinedList.combinedStatusFilter.registered
    },
    {
      id: 2,
      value: users.combinedList.combinedStatusFilter.verified
    },
    {
      id: 3,
      value:
        users.combinedList.combinedStatusFilter
          .pendingBusinessVerification
    },
    {
      id: 4,
      value: users.combinedList.combinedStatusFilter.businessVerified
    },
    {
      id: 5,
      value: users.combinedList.combinedStatusFilter.subscribed
    },
    {
      id: 6,
      value: users.combinedList.combinedStatusFilter.inactive
    }
  ];
  const BUSINESS_TYPE_FILTER = [
    {
      id: 1,
      value: users.businessFilter.factory
    },
    {
      id: 2,
      value: users.businessFilter.trade
    },
    {
      id: 3,
      value: users.businessFilter.both
    }
  ];

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={countriesLookup}
          name="countries"
          label={users.combinedList.filters.countries}
          searchPlaceholder={
            users.combinedList.filters.searchPlaceholders.countries
          }
          selectedOptionsIds={countryFilterValue}
          setSelectedOptionsIds={setCountryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={BUSINESS_TYPE_FILTER.map((filter) => ({
            Id: filter.id,
            Name: filter.value
          }))}
          name="business type"
          label={users.combinedList.filters.business}
          selectedOptionsIds={businessFilterValue}
          setSelectedOptionsIds={setBusinessFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={categoriesLookup}
          name="categories"
          label={users.combinedList.filters.categories}
          searchPlaceholder={
            users.combinedList.filters.searchPlaceholders.categories
          }
          selectedOptionsIds={categoryFilterValue}
          setSelectedOptionsIds={setCategoryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={pricingPlansLookup}
          name="pricing Plan"
          label={users.combinedList.filters.pricingPlan}
          selectedOptionsIds={pricingPlanFilterValue}
          setSelectedOptionsIds={setPricingPlanFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className=" py-3 px-md-5 px-3">
        <ListFilter
          list={COMBINED_STATUS_FILTER.map((filter) => ({
            Id: filter.id,
            Name: filter.value
          }))}
          name="status"
          label={users.combinedList.filters.status}
          selectedOptionsIds={statusFilterValue}
          setSelectedOptionsIds={setStatusFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
        />
      </div>
    </div>
  );

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const handleStatusClass = (combinedUserStatusId, isActive) => {
    let chipClass;
    if (!isActive) {
      chipClass = "red-chip";
    } else {
      switch (combinedUserStatusId) {
        case EXPORTER_STATUS.subscribed:
          chipClass = "green-chip";
          break;
        case EXPORTER_STATUS.verified:
          chipClass = "blue-chip";
          break;
        case EXPORTER_STATUS.businessVerified:
          chipClass = "blue-chip";
          break;
        case EXPORTER_STATUS.registered:
          chipClass = "yellow-chip";
          break;
        case EXPORTER_STATUS.expired:
          chipClass = "light-brown-chip";
          break;
        default:
          chipClass = "";
      }
    }
    return chipClass;
  };

  const nonVerifiedStatuses = [
    EXPORTER_STATUS.registered,
    EXPORTER_STATUS.verified,
    EXPORTER_STATUS.pendingBusinessVerified
  ];

  const renderCombinedTableRow = ({
    UserId,
    CompanyId,
    CompanyName,
    CompanyLogo,
    Country,
    BusinessType,
    CategoriesCount,
    UserStatus,
    IsActive,
    UserStatusId,
    SubscriptionPlan
  }) => {
    const chipClass = handleStatusClass(UserStatusId, IsActive);
    return (
      <TableRow
        className="pointer"
        key={`request-row-${UserId}`}
        onClick={() => {
          navigate(
            ROUTE_PATHS.usersCombinedDetails
              .replace(":id", UserId)
              .replace(":companyId", CompanyId)
          );
        }}>
        <TableCell style={{ width: "20%" }}>
          <div className="d-flex align-items-center gap-3">
            <img
              src={CompanyLogo || noRequestImg}
              onError={onImageError}
              alt={"Company-Logo"}
              className="logo-img  faint-grey-border border-radius-8"
            />
            <TruncateText length={22} text={CompanyName || "__"} />
            {!nonVerifiedStatuses.includes(UserStatusId) && (
              <Verified color="primary" className="ms-1" />
            )}
          </div>
        </TableCell>
        <TableCell style={{ width: "15%" }}>
          <TruncateText text={Country || "__"} />
        </TableCell>
        <TableCell style={{ width: "15%" }}>
          <TruncateText text={BusinessType || "__"} />
        </TableCell>
        <TableCell style={{ width: "15%" }}>
          {`${
            CategoriesCount ?? "0"
          } ${CategoriesCount > 1 ? users.combinedList.categories : users.combinedList.category}`}
        </TableCell>
        <TableCell style={{ width: "15%" }}>
          {!!SubscriptionPlan ? SubscriptionPlan : "--"}
        </TableCell>
        <TableCell>
          <Chip
            label={
              IsActive ? UserStatus : users.combinedList.inactive
            }
            className={` border-radius-8 fw-bold ${chipClass}`}
            sx={{ fontWeight: "semibold", width: "100%" }}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="combined-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-md-center align-items-start">
        <div className="title-count-container d-flex justify-content-center align-items-md-center align-items-start flex-column flex-md-row">
          <div>
            <Typography variant="h5" className="title-color me-1">
              {users.combinedList.combinedListTitle}
            </Typography>
          </div>
          <div className="block-title text-secondary">
            <Typography>{`(${combinedMeta?.count ? combinedMeta?.count : "0"} ${combinedMeta?.count === 1 ? users.combinedList.combinedUser : users.combinedList.combinedUsers})`}</Typography>
          </div>
        </div>
        <div className="title-buttons-container">
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={setIsApplyFilters}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
        </div>
      </div>
      <div className="combined-table-container bg-white d-flex flex-column w-100  p-4 pb-0 px-0 border-radius-8">
        <div className="table-header d-flex px-4 align-items-center w-100 mb-4">
          <SearchInput
            name="CombinedSearch"
            id="CombinedSearch"
            value={searchValue}
            inputClass="border-radius-8"
            inputWrapperClass=" search-input-wrapper search-input-wrapper-width"
            placeholder={users.combinedList.searchPlaceholder}
            onChange={(value) => {
              setPage(1);
              setSearchValue(value);
            }}
          />
        </div>
        <div className="table-content">
          <div>
            {combinedList?.length ? (
              <Table
                className={"px-0 nowrap-table status-action-column"}
                headlines={[
                  users.combinedList.tableHeader.companyName,
                  users.combinedList.tableHeader.country,
                  users.combinedList.tableHeader.businessType,
                  users.combinedList.tableHeader.category,
                  users.combinedList.tableHeader.pricingPlan,
                  users.combinedList.tableHeader.status
                ]}
                rows={
                  !isLoading &&
                  combinedList &&
                  combinedList.map((combinedUser) =>
                    renderCombinedTableRow(combinedUser)
                  )
                }
              />
            ) : (
              <div className="border-top">
                {!isLoading && (
                  <EmptyState
                    subTitle={
                      !!searchValue && !combinedList.length
                        ? users.combinedList.noSearchResults
                        : users.combinedList.noCombinedFound
                    }
                    imgSrc={noImporterImg}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
          {!isLoading && combinedList.length > 0 && (
            <Pagination
              count={combinedMeta?.pages}
              page={page || combinedMeta.currentPage}
              handleChange={(page) => {
                setPage(page);
              }}
              defaultPage={1}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Combined;
